import { useMemo, useState } from "react";
import {
  Search,
  Filter,
  AlertCircle,
  CheckCircle2,
  MoreHorizontal,
  Plus,
  Pencil,
  Trash2,
  PauseCircle,
  XCircle,
  Inbox as InboxIcon,
  X,
} from "lucide-react";
import { useSearchParams } from "react-router";
import { useSubscriptions } from "../../lib/subscriptions/useSubscriptions";
import type {
  Subscription,
  SubscriptionStatus,
} from "../../lib/subscriptions/types";
import { formatAmountCents, formatIsoDate } from "../../lib/format";
import { NewSubscriptionDialog } from "../components/NewSubscriptionDialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const STATUS_OPTIONS: { value: SubscriptionStatus; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "cancelled", label: "Cancelled" },
  { value: "warning", label: "Warning" },
];

function StatusBadge({ status }: { status: SubscriptionStatus }) {
  switch (status) {
    case "active":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-green-50 text-green-700 rounded-lg border border-green-100">
          <CheckCircle2 className="w-3.5 h-3.5" /> Active
        </span>
      );
    case "warning":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-amber-50 text-amber-700 rounded-lg border border-amber-100">
          <AlertCircle className="w-3.5 h-3.5" /> Warning
        </span>
      );
    case "paused":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-slate-50 text-slate-600 rounded-lg border border-slate-200">
          <PauseCircle className="w-3.5 h-3.5" /> Paused
        </span>
      );
    case "cancelled":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-500 rounded-lg border border-slate-200">
          <XCircle className="w-3.5 h-3.5" /> Cancelled
        </span>
      );
  }
}

function formatCadence(cadence: Subscription["cadence"]): string {
  if (!cadence) return "—";
  return cadence.charAt(0).toUpperCase() + cadence.slice(1);
}

export function Subscriptions() {
  const { subscriptions, remove } = useSubscriptions();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-driven filter from the Audit Dashboard drill-down. `?filter=warning`
  // restricts to warning-status rows in addition to whatever the user has
  // manually applied (AND semantics). Dismiss → clear the URL param.
  const urlWarningOnly = searchParams.get("filter") === "warning";
  const [urlFilterDismissed, setUrlFilterDismissed] = useState(false);
  const showUrlBanner = urlWarningOnly && !urlFilterDismissed;

  const [search, setSearch] = useState("");
  const [filterStatuses, setFilterStatuses] = useState<SubscriptionStatus[]>(
    [],
  );
  const [draftFilterStatuses, setDraftFilterStatuses] = useState<
    SubscriptionStatus[]
  >([]);
  const [filterOpen, setFilterOpen] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [editSubscription, setEditSubscription] =
    useState<Subscription | null>(null);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return subscriptions
      .filter((s) => !needle || s.vendor.toLowerCase().includes(needle))
      .filter((s) =>
        filterStatuses.length === 0 ? true : filterStatuses.includes(s.status),
      )
      .filter((s) => (showUrlBanner ? s.status === "warning" : true));
  }, [subscriptions, search, filterStatuses, showUrlBanner]);

  const filterActive = filterStatuses.length > 0;

  function dismissUrlFilter(): void {
    setUrlFilterDismissed(true);
    // Strip the `filter` param but preserve `tab` so we stay on Subscriptions.
    const next = new URLSearchParams(searchParams);
    next.delete("filter");
    setSearchParams(next, { replace: true });
  }

  function toggleDraftStatus(status: SubscriptionStatus) {
    setDraftFilterStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    );
  }

  function handleFilterOpenChange(next: boolean) {
    if (next) setDraftFilterStatuses(filterStatuses);
    setFilterOpen(next);
  }

  function applyFilter() {
    setFilterStatuses(draftFilterStatuses);
    setFilterOpen(false);
  }

  function clearFilter() {
    setDraftFilterStatuses([]);
    setFilterStatuses([]);
  }

  async function handleDelete(sub: Subscription) {
    const ok = window.confirm(
      `Delete subscription "${sub.vendor}"? This can't be undone.`,
    );
    if (!ok) return;
    await remove(sub.id);
  }

  const editInitialValues = editSubscription
    ? {
        vendor: editSubscription.vendor,
        categoryId: editSubscription.categoryId,
        categoryLabel: editSubscription.categoryLabel ?? null,
        cadence: editSubscription.cadence,
        lastChargeCents: editSubscription.lastChargeCents,
        lastChargeOn: editSubscription.lastChargeOn,
        status: editSubscription.status,
        notes: editSubscription.notes ?? null,
      }
    : undefined;

  return (
    <div className="flex flex-col gap-6 h-full pt-6">
      {showUrlBanner && (
        <div
          data-testid="subscriptions-warning-banner"
          className="flex items-center justify-between gap-3 px-4 py-2 bg-amber-50 border border-amber-100 text-amber-800 text-sm rounded-xl"
        >
          <span>Showing only flagged (warning) subscriptions.</span>
          <button
            type="button"
            data-testid="subscriptions-warning-banner-dismiss"
            onClick={dismissUrlFilter}
            aria-label="Dismiss warning filter"
            className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 hover:text-amber-900"
          >
            <X className="w-3.5 h-3.5" /> Dismiss
          </button>
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="subscriptions-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search vendors..."
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 transition-shadow"
            />
          </div>
          <Popover open={filterOpen} onOpenChange={handleFilterOpenChange}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="subscriptions-filter-button"
                className={`flex items-center gap-2 px-4 py-2 font-medium rounded-xl shadow-sm transition-colors text-sm border ${
                  filterActive
                    ? "text-blue-700 bg-blue-50 border-blue-200"
                    : "text-slate-600 bg-white border-slate-200 hover:bg-slate-50"
                }`}
              >
                <Filter className="w-4 h-4" /> Filter
                {filterActive && (
                  <span className="inline-flex items-center justify-center text-[10px] font-semibold h-4 min-w-4 px-1 rounded-full bg-blue-600 text-white">
                    {filterStatuses.length}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              data-testid="subscriptions-filter-popover"
              className="w-64"
            >
              <div className="flex flex-col gap-3">
                <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Status
                </div>
                <div className="flex flex-col gap-1.5">
                  {STATUS_OPTIONS.map((opt) => {
                    const checked = draftFilterStatuses.includes(opt.value);
                    return (
                      <label
                        key={opt.value}
                        className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          data-testid={`subscriptions-filter-status-${opt.value}`}
                          checked={checked}
                          onChange={() => toggleDraftStatus(opt.value)}
                          className="rounded border-slate-300 text-[#0070F2] focus:ring-[#0070F2]/30"
                        />
                        {opt.label}
                      </label>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
                  <button
                    type="button"
                    data-testid="subscriptions-filter-clear"
                    onClick={clearFilter}
                    className="text-xs font-medium text-slate-600 hover:text-slate-900"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    data-testid="subscriptions-filter-apply"
                    onClick={applyFilter}
                    className="px-3 py-1.5 text-xs font-semibold text-white bg-[#0070F2] hover:bg-[#005BCA] rounded-md"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <button
          type="button"
          data-testid="subscriptions-new"
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-2 px-4 py-2 text-white bg-[#0070F2] hover:bg-[#005BCA] font-medium rounded-xl shadow-sm transition-colors text-sm"
        >
          <Plus className="w-4 h-4" /> New Subscription
        </button>
      </div>

      <div className="flex-1 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col min-h-0">
        {visible.length === 0 ? (
          <div
            data-testid="subscriptions-empty-state"
            className="flex-1 flex flex-col items-center justify-center gap-3 text-center p-12 text-slate-500"
          >
            <InboxIcon className="w-10 h-10 text-slate-300" />
            <div className="text-sm font-medium text-slate-700">
              {subscriptions.length === 0
                ? "No subscriptions yet"
                : "No subscriptions match your filters"}
            </div>
            <div className="text-xs text-slate-500">
              {subscriptions.length === 0
                ? "Track recurring software, infrastructure and tool costs."
                : "Try clearing filters to see all subscriptions."}
            </div>
            {subscriptions.length === 0 && (
              <button
                type="button"
                data-testid="subscriptions-empty-new"
                onClick={() => setCreateOpen(true)}
                className="mt-2 flex items-center gap-2 px-4 py-2 text-white bg-[#0070F2] hover:bg-[#005BCA] font-medium rounded-xl shadow-sm transition-colors text-sm"
              >
                <Plus className="w-4 h-4" /> New Subscription
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100">
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Vendor
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Cadence
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Last Charge
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visible.map((sub) => (
                  <tr
                    key={sub.id}
                    data-testid={`subscription-row-${sub.id}`}
                    className="hover:bg-slate-50 transition-colors group"
                  >
                    <td className="px-6 py-4">
                      <div className="font-semibold text-slate-900">
                        {sub.vendor}
                      </div>
                      <div className="text-sm text-slate-500">
                        {sub.categoryLabel?.trim() || "Uncategorized"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                        {formatCadence(sub.cadence)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-slate-900">
                        {sub.lastChargeCents !== null
                          ? formatAmountCents(sub.lastChargeCents)
                          : "—"}
                      </div>
                      <div className="text-sm text-slate-500">
                        {sub.lastChargeOn
                          ? formatIsoDate(sub.lastChargeOn)
                          : "—"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={sub.status} />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            data-testid={`subscription-row-more-${sub.id}`}
                            aria-label={`Actions for ${sub.vendor}`}
                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
                          >
                            <MoreHorizontal className="w-5 h-5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            data-testid={`subscription-row-edit-${sub.id}`}
                            onSelect={() => setEditSubscription(sub)}
                          >
                            <Pencil className="w-4 h-4" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            data-testid={`subscription-row-delete-${sub.id}`}
                            variant="destructive"
                            onSelect={() => {
                              void handleDelete(sub);
                            }}
                          >
                            <Trash2 className="w-4 h-4" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <NewSubscriptionDialog open={createOpen} onOpenChange={setCreateOpen} />

      {editSubscription && (
        <NewSubscriptionDialog
          open={editSubscription !== null}
          onOpenChange={(next) => {
            if (!next) setEditSubscription(null);
          }}
          mode="edit"
          subscriptionId={editSubscription.id}
          initialValues={editInitialValues}
        />
      )}
    </div>
  );
}
